import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NOVITA_VERSIONE, NOVITA_VOCI, NovitaArea, NovitaVoce, daMostrare, vociVisibili } from './supportNovita';
import type { NotificationPrefsApi } from '../../../services/supportApi';

/** Le due chiamate del popup (facoltative: un'api senza non apre mai il popup). */
export type NovitaApi = Pick<NotificationPrefsApi, 'getNovitaVista' | 'setNovitaVista'>;

/** Chiave di sessione: popup già chiuso (anche senza spunta) in questa sessione del browser. */
export const chiaveSessioneNovita = (versione: string): string => `support-novita-chiusa:${versione}`;

// sessionStorage può mancare o lanciare (privacy, quota): assente/errore = non chiusa.
function chiusaInSessione(versione: string): boolean {
  try {
    return window.sessionStorage.getItem(chiaveSessioneNovita(versione)) === '1';
  } catch {
    return false;
  }
}

function ricordaChiusura(versione: string): void {
  try {
    window.sessionStorage.setItem(chiaveSessioneNovita(versione), '1');
  } catch {
    /* niente: il popup ricomparirà al prossimo montaggio */
  }
}

/**
 * Popup «Novità del Centro Ticket».
 * permessi: nomi dei permessi dell'operatore; null finché non sono pronti
 * (nessuna lettura fino ad allora). Una sola lettura per montaggio; un errore
 * non apre il popup e non blocca la pagina. Ogni chiusura si ricorda per la
 * sessione del browser (rientrando nella coda non riappare); «Non mostrare
 * più» salva inoltre la versione sul server, senza attendere: se il
 * salvataggio fallisce, il popup ricomparirà nella prossima sessione.
 */
export function useNovita(
  api: NovitaApi,
  permessi: string[] | null,
  area: NovitaArea,
  tutteLeVoci: NovitaVoce[] = NOVITA_VOCI,
  versione: string = NOVITA_VERSIONE
): { open: boolean; voci: NovitaVoce[]; chiudi: (nonMostrarePiu: boolean) => void } {
  const [daAprire, setDaAprire] = useState(false);
  const letto = useRef(false);
  const apiRef = useRef(api);
  apiRef.current = api;
  const versioneRef = useRef(versione);
  versioneRef.current = versione;

  const pronti = permessi !== null;
  const chiavePermessi = (permessi || []).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- la chiave stringa evita ricalcoli per array nuovi a ogni render
  const voci = useMemo(() => vociVisibili(tutteLeVoci, permessi || [], area), [tutteLeVoci, chiavePermessi, area]);

  useEffect(() => {
    // Niente annullamento nel cleanup: con StrictMode l'effetto gira due volte e
    // la seconda non rilegge (letto), quindi la prima risposta deve valere.
    if (!pronti || letto.current) return;
    letto.current = true;
    const v = versioneRef.current;
    if (chiusaInSessione(v)) return;
    const get = apiRef.current.getNovitaVista;
    if (!get) return;
    // Promise.resolve: anche un'eccezione sincrona o un valore non-promessa finiscono nel catch/then.
    Promise.resolve()
      .then(() => get())
      .then((r) => { if (r && daMostrare(r.versioneVista, v)) setDaAprire(true); })
      .catch(() => { /* mai bloccare la pagina: il popup resta chiuso */ });
  }, [pronti]);

  const chiudi = useCallback((nonMostrarePiu: boolean) => {
    setDaAprire(false);
    const v = versioneRef.current;
    ricordaChiusura(v);
    const set = apiRef.current.setNovitaVista;
    if (nonMostrarePiu && set) {
      Promise.resolve()
        .then(() => set(v))
        .catch(() => { /* silenzio: ricomparirà nella prossima sessione */ });
    }
  }, []);

  return { open: daAprire && voci.length > 0, voci, chiudi };
}
